import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Download, FolderTree, ImagePlus, PackagePlus, Plus, RefreshCw, Trash2, Upload } from "lucide-react";
import { api } from "../../services/api";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { ToastStack, useToast } from "./useToast";
import { resolveProductImage } from "../../utils/productAssets";
import MediaLibraryPicker from "../../components/media/MediaLibraryPicker";
import { useAdminResource } from "./useAdminResource";
import { useServerPagination } from "./useServerPagination";
import {
  BulkUploadBar,
  getErrorMessage,
  formatChannels,
  normalizeProduct,
  PriceUpdatePanel,
  ProductFilterBar,
  ProductRow,
  PRODUCT_CHANNEL_LABELS,
  PRODUCT_FILTER_TO_PARAM,
  useDebounce,
} from "./ProductandInventory";

const emptyCategory = {
  name: "",
  slug: "",
  wcId: "",
  parentId: 0,
  image: "",
  description: "",
  display: "default",
  menuOrder: 0,
  sortOrder: 100,
  count: 0,
  active: true,
  showInHeader: true,
};

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getCategoryKey(category) {
  return String(category?._id || category?.wcId || category?.slug || category?.name || "");
}

function flattenCategories(items = [], depth = 0, parentNames = []) {
  return items.flatMap((category) => {
    const children = category.subcategories || category.children || [];
    const item = {
      ...category,
      depth,
      childrenCount: children.length,
      pathLabel: [...parentNames, category.name].filter(Boolean).join(" / "),
    };

    return [item, ...flattenCategories(children, depth + 1, [...parentNames, category.name])];
  });
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function categoryToForm(category = emptyCategory) {
  return {
    name: category.name || "",
    slug: category.slug || "",
    wcId: category.wcId || "",
    parentId: category.parentId || 0,
    image: category.image || "",
    description: category.description || "",
    display: category.display || "default",
    menuOrder: category.menuOrder ?? 0,
    sortOrder: category.sortOrder ?? 100,
    count: category.count ?? 0,
    active: category.active !== false,
    showInHeader: category.showInHeader !== false,
  };
}

function buildPayload(form) {
  const payload = {
    name: form.name.trim(),
    slug: form.slug.trim() || slugify(form.name),
    parentId: toNumber(form.parentId, 0),
    image: form.image,
    description: form.description,
    display: form.display || "default",
    menuOrder: toNumber(form.menuOrder, 0),
    sortOrder: toNumber(form.sortOrder, 100),
    count: toNumber(form.count, 0),
    active: form.active !== false,
    showInHeader: form.showInHeader !== false,
  };

  if (form.wcId) payload.wcId = toNumber(form.wcId, undefined);
  return payload;
}

function getCategoryProductFilter(category, page, pageSize) {
  if (!category) return null;

  const params = new URLSearchParams({
    limit: String(pageSize),
    page: String(page),
  });
  const isChild = Number(category.parentId) > 0 || Number(category.depth) > 0;

  if (isChild && category.slug) params.set("subcategorySlug", category.slug);
  else if (category.slug) params.set("categorySlug", category.slug);
  else if (category.name) params.set("category", category.name);

  return params;
}

export default function CategoryManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [categoryTree, setCategoryTree] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyCategory);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [activeProductFilter, setActiveProductFilter] = useState("All");
  const [pricePercent, setPricePercent] = useState(10);
  const [adjustments, setAdjustments] = useState({});
  const [connectedChannels, setConnectedChannels] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState(() => new Set());
  const [uploadingKey, setUploadingKey] = useState("");
  const [bulkUploading, setBulkUploading] = useState("");
  const { page, pageSize, setPage, setPageSize, resetPage } = useServerPagination(25);
  const debouncedProductSearch = useDebounce(productSearch, 350);
  const { dismiss, toast, toasts } = useToast();

  const flatCategories = useMemo(() => flattenCategories(categoryTree), [categoryTree]);
  const deepLinkedCategoryId = useMemo(
    () => new URLSearchParams(location.search).get("edit") || "",
    [location.search]
  );

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/product-categories");
      setCategoryTree(data.categories || []);
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const selectedCategory = flatCategories.find((category) => getCategoryKey(category) === selectedId);

  useEffect(() => {
    resetPage();
    setSelectedProductIds(new Set());
  }, [selectedId, resetPage]);

  useEffect(() => {
    resetPage();
  }, [activeProductFilter, debouncedProductSearch, resetPage]);

  const categoryProductParams = useMemo(() => {
    const params = getCategoryProductFilter(selectedCategory, page, pageSize);
    if (!params) return null;

    if (debouncedProductSearch.trim()) {
      params.set("search", debouncedProductSearch.trim());
    }

    const filterParam = PRODUCT_FILTER_TO_PARAM[activeProductFilter];
    if (filterParam) {
      Object.entries(filterParam).forEach(([key, value]) => params.set(key, value));
    }

    return params;
  }, [activeProductFilter, debouncedProductSearch, page, pageSize, selectedCategory]);

  const loadConnectedChannels = useCallback(async () => {
    try {
      const response = await api.get("/channels");
      const channels = Array.isArray(response?.channels) ? response.channels : [];
      setConnectedChannels(channels.filter((channel) => channel.status === "connected").map((channel) => channel.name));
    } catch {
      setConnectedChannels([]);
    }
  }, []);

  useEffect(() => {
    loadConnectedChannels();
  }, [loadConnectedChannels]);

  const loadCategoryProducts = useCallback(async () => {
    if (!categoryProductParams) {
      return {
        products: [],
        stats: { lowStockCount: 0 },
        pagination: {
          page,
          pageSize,
          totalItems: 0,
          totalPages: 1,
          setPage,
          setPageSize,
        },
      };
    }

    const response = await api.get(`/products?${categoryProductParams.toString()}`);
    const apiPagination = response?.pagination || {};

    return {
      products: Array.isArray(response?.products) ? response.products.map(normalizeProduct) : [],
      stats: {
        lowStockCount: 0,
        ...(response?.stats || {}),
      },
      pagination: {
        page: Number(apiPagination.page || page),
        pageSize: Number(apiPagination.limit || apiPagination.pageSize || pageSize),
        totalItems: Number(apiPagination.total || apiPagination.totalItems || 0),
        totalPages: Number(apiPagination.totalPages || apiPagination.pages || 1),
        hasPrevPage: Boolean(apiPagination.hasPrevPage),
        hasNextPage: Boolean(apiPagination.hasNextPage),
        setPage,
        setPageSize,
      },
    };
  }, [categoryProductParams, page, pageSize, setPage, setPageSize]);

  const {
    data: productData,
    error: productError,
    loading: productsLoading,
    reload: reloadProducts,
    setData: setProductData,
  } = useAdminResource(loadCategoryProducts, {
    products: [],
    stats: { lowStockCount: 0 },
    pagination: {
      page,
      pageSize,
      totalItems: 0,
      totalPages: 1,
      setPage,
      setPageSize,
    },
  });

  const categoryProducts = useMemo(() => (Array.isArray(productData?.products) ? productData.products : []), [productData?.products]);

  const categoryProductStats = useMemo(() => ({
    lowStockCount: 0,
    ...(productData?.stats || {}),
  }), [productData?.stats]);

  const categoryProductsPagination = useMemo(() => {
    const apiPagination = productData?.pagination || {};

    return {
      page: Number(apiPagination.page || page),
      pageSize: Number(apiPagination.pageSize || apiPagination.limit || pageSize),
      totalItems: Number(apiPagination.totalItems || apiPagination.total || 0),
      totalPages: Number(apiPagination.totalPages || apiPagination.pages || 1),
      hasPrevPage: Boolean(apiPagination.hasPrevPage),
      hasNextPage: Boolean(apiPagination.hasNextPage),
      disabled: productsLoading,
      setPage,
      setPageSize,
    };
  }, [page, pageSize, productData?.pagination, productsLoading, setPage, setPageSize]);

  const uploadTargets = useMemo(() => {
    const connected = new Set(connectedChannels);
    const targets = [];

    if (connected.has("ebay")) {
      targets.push({
        key: "ebay",
        label: "eBay",
        channels: ["ebay"],
      });
    }

    return targets;
  }, [connectedChannels]);

  useEffect(() => {
    if (productError && !productsLoading) {
      toast(`Could not load products: ${productError}`, "error");
    }
  }, [productError, productsLoading, toast]);

  useEffect(() => {
    setSelectedProductIds((current) => {
      const visibleIds = new Set(categoryProducts.map((product) => product.id));
      return new Set([...current].filter((id) => visibleIds.has(id)));
    });
  }, [categoryProducts]);

  const selectedProducts = useMemo(() => {
    return categoryProducts.filter((product) => selectedProductIds.has(product.id));
  }, [categoryProducts, selectedProductIds]);

  const allCurrentPageSelected = useMemo(() => (
    categoryProducts.length > 0 && categoryProducts.every((product) => selectedProductIds.has(product.id))
  ), [categoryProducts, selectedProductIds]);

  const mappedChannels = useMemo(() => {
    return new Set(categoryProducts.flatMap((product) => (Array.isArray(product.channels) ? product.channels : []))).size;
  }, [categoryProducts]);

  const toggleSelectProduct = useCallback((productId) => {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }, []);

  const toggleSelectCurrentPage = useCallback(() => {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      if (categoryProducts.length && categoryProducts.every((product) => next.has(product.id))) {
        categoryProducts.forEach((product) => next.delete(product.id));
      } else {
        categoryProducts.forEach((product) => next.add(product.id));
      }
      return next;
    });
  }, [categoryProducts]);

  const clearSelection = useCallback(() => {
    setSelectedProductIds(new Set());
  }, []);

  const editProduct = useCallback((product) => {
    navigate(`/dashboard/products/${product.id}/edit`);
  }, [navigate]);

  const duplicateProduct = useCallback(async (product) => {
    if (!window.confirm(`Duplicate product ${product.sku}? A draft copy will be created.`)) return;

    try {
      const response = await api.post(`/products/${product.id}/duplicate`, {});
      const duplicated = response?.product;
      if (!duplicated?._id) throw new Error("Duplicate response was invalid.");
      toast(`Created draft copy ${duplicated.sku}`, "success");
      navigate(`/dashboard/products/${duplicated._id}/edit`);
    } catch (err) {
      toast(getErrorMessage(err, "Could not duplicate product"), "error");
    }
  }, [navigate, toast]);

  const updateProductInTable = useCallback((rawProduct) => {
    if (!rawProduct) return;
    const updated = normalizeProduct(rawProduct);

    setProductData((current) => ({
      ...current,
      products: Array.isArray(current.products)
        ? current.products.map((item) => (item.id === updated.id ? updated : item))
        : [],
    }));
  }, [setProductData]);

  const deleteProduct = useCallback(async (product) => {
    if (!window.confirm(`Delete product ${product.sku}? This removes it from the website catalog.`)) return;

    try {
      await api.delete(`/products/${product.id}`);
      toast(`Deleted product ${product.sku}`, "success");

      if (categoryProducts.length === 1 && categoryProductsPagination.page > 1) {
        setPage((currentPage) => Math.max(1, currentPage - 1));
      } else {
        await reloadProducts();
      }
      await loadCategories();
    } catch (err) {
      toast(getErrorMessage(err, "Could not delete product"), "error");
    }
  }, [categoryProducts.length, categoryProductsPagination.page, loadCategories, reloadProducts, setPage, toast]);

  const uploadOneChannel = useCallback(async (product, channel) => {
    if (channel === "ebay") {
      if (!product.ebayCategoryId) {
        throw new Error(`${product.sku} needs an eBay category before upload. Open Edit Product, search/select the eBay category, save, then upload again.`);
      }
      return api.post("/channels/ebay/products/upload", { productId: product.id });
    }

    throw new Error(`Unsupported channel: ${channel}`);
  }, []);

  const uploadProductToTarget = useCallback(async (product, target) => {
    const key = `${product.id}:${target.key}`;

    try {
      setUploadingKey(key);
      const results = await Promise.allSettled(target.channels.map((channel) => uploadOneChannel(product, channel)));
      const successResults = results.filter((result) => result.status === "fulfilled");
      const failedResults = results.filter((result) => result.status === "rejected");
      const updatedProduct = successResults.find((result) => result.value?.product)?.value?.product || null;

      if (updatedProduct) updateProductInTable(updatedProduct);

      if (failedResults.length) {
        toast(getErrorMessage(failedResults[0]?.reason, `${product.sku} upload failed for ${target.label}`), "error");
      }

      if (successResults.length) {
        toast(`${product.sku} uploaded to ${formatChannels(target.channels)}`, "success");
      }
    } catch (err) {
      toast(getErrorMessage(err, "Could not upload product"), "error");
    } finally {
      setUploadingKey("");
    }
  }, [toast, uploadOneChannel, updateProductInTable]);

  const bulkUploadToTarget = useCallback(async (target) => {
    if (!selectedProducts.length) {
      toast("Please select at least one product", "error");
      return;
    }

    if (!window.confirm(`Upload ${selectedProducts.length} selected product${selectedProducts.length !== 1 ? "s" : ""} to ${target.label}?`)) return;

    try {
      setBulkUploading(target.key);
      let successCount = 0;
      let failCount = 0;

      for (const product of selectedProducts) {
        const results = await Promise.allSettled(target.channels.map((channel) => uploadOneChannel(product, channel)));
        if (results.some((result) => result.status === "fulfilled")) successCount += 1;
        else failCount += 1;
      }

      if (successCount) toast(`${successCount} product${successCount !== 1 ? "s" : ""} uploaded to ${target.label}`, "success");
      if (failCount) toast(`${failCount} product${failCount !== 1 ? "s" : ""} failed`, "error");

      clearSelection();
      await reloadProducts();
    } catch (err) {
      toast(getErrorMessage(err, "Bulk upload failed"), "error");
    } finally {
      setBulkUploading("");
    }
  }, [clearSelection, reloadProducts, selectedProducts, toast, uploadOneChannel]);

  const adjustInventory = useCallback(async (product) => {
    const quantityDelta = Number(adjustments[product.id] || 0);
    if (!quantityDelta) return;

    try {
      const response = await api.post(`/products/${product.id}/inventory-adjustment`, {
        quantityDelta,
        reason: "Admin manual adjustment",
      });

      if (!response?.product) throw new Error("Inventory adjustment response was invalid.");
      updateProductInTable(response.product);
      setAdjustments((current) => ({ ...current, [product.id]: "" }));
      toast(`Inventory updated for ${product.sku}`, "success");
    } catch (err) {
      toast(getErrorMessage(err, "Could not update inventory"), "error");
    }
  }, [adjustments, toast, updateProductInTable]);

  const applyJukiIncrease = useCallback(async () => {
    try {
      const response = await api.post("/products/price-increase", {
        brandLine: "Juki",
        percent: Number(pricePercent),
      });

      toast(response?.message || "Price increase applied", "success");
      await reloadProducts();
    } catch (err) {
      toast(getErrorMessage(err, "Could not apply price increase"), "error");
    }
  }, [pricePercent, reloadProducts, toast]);

  const exportProductsCsv = useCallback(() => {
    const rows = [
      ["SKU", "Product", "Brand", "Price", "Stock", "Channels", "Supplier", "Status"],
      ...categoryProducts.map((product) => [
        product.sku,
        product.name,
        product.brandLine,
        Number(product.price || 0).toFixed(2),
        product.stock,
        Array.isArray(product.channels) ? product.channels.join("|") : "",
        product.supplier,
        product.status,
      ]),
    ];

    const csv = rows.map((row) => row.map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${selectedCategory?.slug || "category"}-products-current-page.csv`;
    link.click();

    URL.revokeObjectURL(url);
    toast("Product CSV exported", "success");
  }, [categoryProducts, selectedCategory?.slug, toast]);

  const visibleCategories = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return flatCategories;

    return flatCategories.filter((category) =>
      [category.name, category.slug, category.pathLabel, category.wcId]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [flatCategories, search]);

  const parentOptions = useMemo(() => {
    return flatCategories.filter((category) => {
      if (!category.wcId) return false;
      return getCategoryKey(category) !== selectedId;
    });
  }, [flatCategories, selectedId]);

  const startNew = () => {
    setSelectedId(null);
    setForm(emptyCategory);
  };

  const startChild = (category) => {
    const parentId = category.wcId || 0;
    setSelectedId(null);
    setForm({
      ...emptyCategory,
      parentId,
      showInHeader: false,
      menuOrder: (category.childrenCount || 0) + 1,
    });
  };

  const editCategory = (category) => {
    setSelectedId(getCategoryKey(category));
    setForm(categoryToForm(category));
  };

  useEffect(() => {
    if (!deepLinkedCategoryId || loading || !flatCategories.length) return;

    const nextCategory = flatCategories.find((category) => getCategoryKey(category) === deepLinkedCategoryId);
    if (!nextCategory || selectedId === getCategoryKey(nextCategory)) return;

    setSelectedId(getCategoryKey(nextCategory));
    setForm(categoryToForm(nextCategory));
  }, [deepLinkedCategoryId, flatCategories, loading, selectedId]);

  const updateForm = (event) => {
    const { checked, name, type, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "name" && !selectedId ? { slug: slugify(value) } : {}),
      ...(name === "parentId" && Number(value) > 0 ? { showInHeader: false } : {}),
    }));
  };

  const saveCategory = async (event) => {
    event.preventDefault();
    try {
      const payload = buildPayload(form);
      const data = selectedCategory?._id
        ? await api.patch(`/product-categories/${selectedCategory._id}`, payload)
        : await api.post("/product-categories", payload);

      await loadCategories();
      setSelectedId(getCategoryKey(data.category));
      setForm(categoryToForm(data.category));
      toast(`${selectedCategory ? "Updated" : "Created"} category ${data.category.name}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const deleteCategory = async (category) => {
    if (!category?._id) return;
    if (!window.confirm(`Delete category "${category.name}"? Products using this slug will need reassignment.`)) return;
    try {
      await api.delete(`/product-categories/${category._id}`);
      await loadCategories();
      if (selectedId === getCategoryKey(category)) startNew();
      toast(`Deleted ${category.name}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  return (
    <div className="p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />
      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#4391d1]">Catalog Taxonomy</p>
          <h1 className="text-3xl font-black text-gray-900">Product Categories</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
            Manage WooCommerce category records, parent links, storefront visibility, images, and catalog ordering.
          </p>
        </div>
        <WorkflowToolbar actions={[{ label: "Add Category", primary: true, onClick: startNew }, { label: "Refresh", onClick: loadCategories }]} />
      </div>

      <div className="grid gap-6 xl:grid-cols-[460px_1fr]">
        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-5">
            <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold outline-none focus:border-[#4391d1]" onChange={(event) => setSearch(event.target.value)} placeholder="Search categories, slugs, or IDs" value={search} />
          </div>
          <div className="max-h-[720px] overflow-y-auto p-3">
            {loading ? (
              <p className="p-4 text-sm font-bold text-gray-400">Loading categories...</p>
            ) : visibleCategories.length ? (
              visibleCategories.map((category) => {
                const isChild = Number(category.parentId) > 0 || category.depth > 0;
                return (
                <button
                  className={`mb-2 w-full rounded-xl border text-left transition ${isChild ? "py-2.5 pr-3" : "p-4"} ${selectedId === getCategoryKey(category) ? "border-[#4391d1] bg-blue-50" : isChild ? "border-gray-100 bg-gray-50 hover:border-blue-200 hover:bg-white" : "border-gray-100 bg-white hover:border-blue-200"}`}
                  key={getCategoryKey(category)}
                  onClick={() => editCategory(category)}
                  style={{ paddingLeft: `${isChild ? 22 + category.depth * 24 : 16}px` }}
                  type="button"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        {isChild && <span className="h-2 w-2 shrink-0 rounded-full bg-blue-300" />}
                        <p className={`truncate m-0 ${isChild ? "text-sm font-extrabold text-gray-700" : "font-black text-gray-900"}`}>{category.name}</p>
                      </div>
                      <p className="mt-1 mb-0 truncate text-xs font-bold text-gray-400">{category.pathLabel || category.slug}</p>
                    </div>
                    <StatusPill tone={category.active ? (isChild ? "neutral" : "good") : "neutral"}>{isChild ? " child" : category.active ? "parent" : "off"}</StatusPill>
                  </div>
                  <div className={`mt-2 flex flex-wrap gap-2 font-semibold text-gray-500 ${isChild ? "text-[11px]" : "text-xs"}`}>
                    <span>{category.childrenCount || 0} children</span>
                    <span>WC {category.wcId || "local"}</span>
                    <span>{toNumber(category.count, 0)} products</span>
                  </div>
                </button>
                );
              })
            ) : (
              <p className="p-4 text-sm font-bold text-gray-400">No categories match your search.</p>
            )}
          </div>
        </section>

        <form className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm" onSubmit={saveCategory}>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="rounded-xl bg-blue-50 p-2 text-[#4391d1]"><FolderTree size={18} /></span>
              <div>
                <h2 className="text-lg font-black text-gray-900">{selectedId ? "Edit Category" : "Add Category"}</h2>
                {selectedCategory && <p className="text-xs font-bold text-gray-400">{selectedCategory.pathLabel || selectedCategory.slug}</p>}
              </div>
            </div>
            {selectedCategory?.wcId && (
              <button className="inline-flex items-center gap-2 rounded-lg bg-gray-950 px-3 py-2 text-xs font-black text-white" onClick={() => startChild(selectedCategory)} type="button">
                <Plus size={14} /> Add Child
              </button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {[
              ["name", "Name"],
              ["slug", "Slug"],
              ["wcId", "WooCommerce ID"],
              ["menuOrder", "Menu order"],
              ["sortOrder", "Fallback sort"],
              ["count", "Imported count"],
            ].map(([name, label]) => (
              <label className="block" key={name}>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">{label}</span>
                <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]" name={name} onChange={updateForm} required={["name", "slug"].includes(name)} type={["wcId", "menuOrder", "sortOrder", "count"].includes(name) ? "number" : "text"} value={form[name] || ""} />
              </label>
            ))}

            <label className="block">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Parent category</span>
              <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]" name="parentId" onChange={updateForm} value={form.parentId || 0}>
                <option value={0}>Root category</option>
                {parentOptions.map((category) => (
                  <option key={getCategoryKey(category)} value={category.wcId}>
                    {"- ".repeat(category.depth)}{category.name} (WC {category.wcId})
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Display mode</span>
              <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]" name="display" onChange={updateForm} value={form.display || "default"}>
                <option value="default">Default</option>
                <option value="products">Products</option>
                <option value="subcategories">Subcategories</option>
                <option value="both">Both</option>
              </select>
            </label>

            <div className="md:col-span-2 rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <div className="grid gap-4 md:grid-cols-[150px_1fr]">
                <div className="flex h-36 items-center justify-center rounded-xl border border-gray-200 bg-white">
                  {form.image ? (
                    <img alt="Category" className="h-full w-full object-contain p-3" src={resolveProductImage(form.image)} />
                  ) : (
                    <ImagePlus className="text-gray-300" size={34} />
                  )}
                </div>
                <div>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Category image</span>
                  <p className="mb-3 text-sm font-semibold leading-6 text-gray-500">
                    Upload or paste the image used for storefront category cards and category navigation.
                  </p>
                  <MediaLibraryPicker
                    buttonClassName="inline-flex items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600"
                    buttonLabel="Choose image"
                    folder="categories"
                    onSelect={(image) => {
                      setForm((current) => ({ ...current, image }));
                      toast("Category image selected", "success");
                    }}
                    selectedUrls={form.image ? [form.image] : []}
                    source="categories"
                  />
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-xs font-semibold text-gray-500" name="image" onChange={updateForm} placeholder="/uploads/categories/..." value={form.image || ""} />
                    {form.image && (
                      <button className="rounded-xl border border-red-100 bg-red-50 p-3 text-red-600" onClick={() => setForm((current) => ({ ...current, image: "" }))} type="button">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <label className="block md:col-span-2">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Description</span>
              <textarea className="min-h-24 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]" name="description" onChange={updateForm} value={form.description || ""} />
            </label>

            <label className="flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-3 text-sm font-bold text-[#1278b5]">
              <input checked={form.active} name="active" onChange={updateForm} type="checkbox" /> Active
            </label>
            <label className="flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-3 text-sm font-bold text-[#1278b5]">
              <input checked={form.showInHeader} disabled={Number(form.parentId) > 0} name="showInHeader" onChange={updateForm} type="checkbox" /> Show in header
            </label>
          </div>

          {selectedCategory?.childrenCount > 0 && (
            <section className="mt-5 rounded-xl border border-gray-100 bg-gray-50 p-3">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-black text-gray-900">Child categories</h3>
                <span className="rounded-full bg-white px-2 py-1 text-[10px] font-black uppercase tracking-wide text-gray-400">
                  Parent: {selectedCategory.name}
                </span>
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {flatCategories
                  .filter((category) => Number(category.parentId) === Number(selectedCategory.wcId))
                  .map((category) => (
                    <button className="flex min-w-0 items-center justify-between gap-2 rounded-lg border border-blue-100 bg-white px-3 py-2 text-left transition hover:border-blue-300 hover:bg-blue-50" key={getCategoryKey(category)} onClick={() => editCategory(category)} type="button">
                      <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="h-2 w-2 shrink-0 rounded-full bg-[#4391d1]" />
                          <p className="truncate text-sm font-black text-gray-800">{category.name}</p>
                        </div>
                        <p className="truncate text-[11px] font-bold text-gray-400">{category.slug}</p>
                      </div>
                      <span className="shrink-0 rounded-full bg-blue-50 px-2 py-1 text-[10px] font-black text-[#1278b5]">child</span>
                    </button>
                  ))}
              </div>
            </section>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button className="rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white" type="submit">{selectedId ? "Update Category" : "Create Category"}</button>
            {selectedCategory?._id && <button className="rounded-xl border border-red-100 bg-red-50 px-5 py-3 text-sm font-black text-red-600" onClick={() => deleteCategory(selectedCategory)} type="button">Delete Category</button>}
          </div>
        </form>
      </div>

      {selectedCategory && (
        <section className="mt-6 space-y-5">
          <div className="grid gap-5 lg:grid-cols-4">
            <InsightCard title="Category SKUs" value={categoryProductsPagination.totalItems} note="Matching current category filter" />
            <InsightCard title="Low Stock" value={categoryProductStats.lowStockCount} note="Below threshold globally" tone="warn" />
            <InsightCard title="Selected" value={selectedProductIds.size} note="Ready for bulk upload" />
            <InsightCard
              title="Connected"
              value={connectedChannels.map((channel) => PRODUCT_CHANNEL_LABELS[channel] || channel).join(", ") || "None"}
              note={`Mapped channels on page: ${mappedChannels}`}
              tone="good"
            />
          </div>

          <section className="grid gap-5 xl:grid-cols-[1fr_360px]">
            <ProductFilterBar
              active={activeProductFilter}
              onFilterChange={setActiveProductFilter}
              search={productSearch}
              onSearchChange={setProductSearch}
            />
            <PriceUpdatePanel percent={pricePercent} onPercentChange={setPricePercent} onApply={applyJukiIncrease} />
          </section>

          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-5">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-black text-gray-900">Product Table</h2>
                  <StatusPill tone={Number(selectedCategory.parentId) > 0 || selectedCategory.depth > 0 ? "info" : "good"}>
                    {Number(selectedCategory.parentId) > 0 || selectedCategory.depth > 0 ? "subcategory" : "category"}
                  </StatusPill>
                </div>
                {!productsLoading && (
                  <p className="mt-0.5 text-xs text-gray-400">
                    {categoryProductsPagination.totalItems} product{categoryProductsPagination.totalItems !== 1 ? "s" : ""} in {selectedCategory.name}
                    {activeProductFilter !== "All" ? ` - ${activeProductFilter}` : ""}
                    {debouncedProductSearch ? ` - "${debouncedProductSearch}"` : ""}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <button className="inline-flex items-center gap-2 rounded-xl bg-[#4391d1] px-3 py-2 text-xs font-black text-white hover:bg-blue-600" onClick={() => navigate("/dashboard/products/new")} type="button">
                  <PackagePlus size={14} /> Add Product
                </button>
                <button className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={exportProductsCsv} type="button">
                  <Download size={14} /> Export CSV
                </button>
                <button className="inline-flex cursor-not-allowed items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-black text-gray-400" disabled title="CSV import coming soon." type="button">
                  <Upload size={14} /> Import CSV
                </button>
                <button className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={async () => { await loadConnectedChannels(); await reloadProducts(); }} type="button">
                  <RefreshCw size={14} className={productsLoading ? "animate-spin" : ""} /> Refresh
                </button>
              </div>
            </div>

            <BulkUploadBar selectedCount={selectedProductIds.size} uploadTargets={uploadTargets} bulkUploading={bulkUploading} onBulkUpload={bulkUploadToTarget} onClearSelection={clearSelection} />

            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full text-left">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-400">
                  <tr>
                    <th className="w-10 whitespace-nowrap px-4 py-3 font-black">
                      <input checked={allCurrentPageSelected} className="h-3.5 w-3.5 rounded border-gray-300 text-[#4391d1] focus:ring-[#4391d1]" disabled={!categoryProducts.length || productsLoading} onChange={toggleSelectCurrentPage} type="checkbox" />
                    </th>
                    <th className="whitespace-nowrap px-4 py-3 font-black">Product</th>
                    <th className="whitespace-nowrap px-4 py-3 font-black">Marketplace</th>
                    <th className="whitespace-nowrap px-4 py-3 font-black">Status / Brand</th>
                    <th className="whitespace-nowrap px-4 py-3 font-black">Price</th>
                    <th className="w-24 whitespace-nowrap px-3 py-3 font-black">Stock</th>
                    <th className="w-12 px-4 py-3 font-black"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {productsLoading && (
                    <tr>
                      <td className="px-6 py-10 text-center text-sm font-bold text-gray-400" colSpan={7}>
                        <div className="flex items-center justify-center gap-2">
                          <RefreshCw size={14} className="animate-spin" />
                          Loading products...
                        </div>
                      </td>
                    </tr>
                  )}

                  {!productsLoading && categoryProducts.length === 0 && (
                    <tr>
                      <td className="px-6 py-10 text-center text-sm font-bold text-gray-400" colSpan={7}>
                        {debouncedProductSearch || activeProductFilter !== "All"
                          ? "No products match this filter. Try clearing the search or changing the filter."
                          : "No products are assigned to this category yet."}
                      </td>
                    </tr>
                  )}

                  {!productsLoading && categoryProducts.map((product) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      adjustment={adjustments[product.id]}
                      checked={selectedProductIds.has(product.id)}
                      connectedChannels={connectedChannels}
                      uploadTargets={uploadTargets}
                      uploadingKey={uploadingKey}
                      onToggleSelect={toggleSelectProduct}
                      onAdjustmentChange={setAdjustments}
                      onAdjustInventory={adjustInventory}
                      onDuplicate={duplicateProduct}
                      onEdit={editProduct}
                      onDelete={deleteProduct}
                      onUploadProduct={uploadProductToTarget}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <PaginationControls pagination={categoryProductsPagination} />
          </section>
        </section>
      )}
    </div>
  );
}
